import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, matchPath } from 'react-router-dom';
import withAuthContext from '../../context/auth/AuthConsumer';
import { IAuthContext } from '../../context/auth/auth-context';
import Sidebar from './Sidebar';
import './index.scss';
import DeepSearchInput from './DeepSearchInput';
import TopRightNav from './TopRightNav';
import ChatWindow from './ChatWindow';
import { saveChats } from '../../services/firebase';
import { ISavedDeepChatListItem } from '../../types/deep-research';
import { pollResearchResult, streamResearch } from './researchStream';
import { FileDropOverlay } from '../../components/ui';
import useFileDrop from '../../hooks/useFileDrop';
import { IMAGE_MIME_TYPES, partitionFiles } from '../../utils/fileAccept';
import notifyRejectedFiles from '../../utils/notifyRejectedFiles';
import { isExxonMobileEmail } from '../Validator';

const DEEP_RESEARCH_DRAFT_KEY_PREFIX = 'enviroDeepResearchDraft';
const DEEP_RESEARCH_CHAT_TYPE = 'deep-research';
/** Token flush cadence — keeps long reports from re-rendering on every chunk. */
const TOKEN_FLUSH_MS = 50;

function serializeDeepResearchMessages(messages: any[]): any[] {
  return messages.map((m) => ({
    content: typeof m.content === 'string' ? m.content : '',
    role: m.role,
    status: m.status,
    format: m.format,
    pdf: m.pdf,
    route: m.route,
    file_urls: m.file_urls,
    attachFiles: Array.isArray(m.attachFiles)
      ? m.attachFiles.map((f: any) =>
          f?.preview && String(f.preview).length > 12000
            ? { name: f.name, type: f.type, preview: '' }
            : { name: f?.name, type: f?.type, preview: f?.preview },
        )
      : undefined,
  }));
}

function payloadToChatData(payload: any): any[] {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload.deepResearchMessages) && payload.deepResearchMessages.length) {
    return payload.deepResearchMessages.map((m: any) => ({
      content: m.content || '',
      role: m.role,
      status: m.status,
      // Absent on everything saved before the SSE migration — those are HTML.
      format: m.format,
      pdf: m.pdf,
      route: m.route,
      file_urls: m.file_urls,
      attachFiles: m.attachFiles || [],
    }));
  }
  const raw = payload.chatData;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: any[] = [];
  for (const item of raw) {
    if (!item || !item.q) {
      continue;
    }
    const q = item.q;
    const title = typeof q.title === 'string' ? q.title : '';
    if (title) {
      out.push({
        content: title.includes('<') ? title : title.replace(/\n/g, '<br/>'),
        role: 'user',
        attachFiles: item.attachFiles || [],
      });
    }
    ['a1', 'a2', 'a3'].forEach((key) => {
      const block = q[key];
      if (typeof block === 'string' && block.trim()) {
        out.push({
          content: block.replace(/\n/g, '<br/>'),
        });
      }
    });
  }
  return out;
}

function hasAssistantResults(messages: any[]): boolean {
  if (!Array.isArray(messages)) {
    return false;
  }
  return messages.some((m) => {
    if (!m || m.role === 'user') {
      return false;
    }
    const content = typeof m.content === 'string' ? m.content.trim() : '';
    if (!content) {
      return false;
    }
    return content !== 'No response received.';
  });
}

function extractFirstUserQuestion(messages: any[]): string {
  if (!Array.isArray(messages)) {
    return '';
  }
  const userMsg = messages.find((m) => m && m.role === 'user');
  const raw = typeof userMsg?.content === 'string' ? userMsg.content : '';
  return raw.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').trim();
}

/** Only the search box is persisted for refresh; chat / plan stay in memory. */
interface IDeepResearchDraft {
  searchText: string;
  updatedAt: number;
}

const EnviroDeepResearch: React.FC<{ context: IAuthContext }> = ({ context }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [chatData, setChatData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // True only while the first message's saveChats create is in flight.
  const [isSavingChat, setIsSavingChat] = useState(false);
  const [chatId, setChatId] = useState<string | number | null>(null);
  // Transient progress line from the stream's `status` events — never persisted.
  const [progressLine, setProgressLine] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Mirrors `isBusy` for re-entrancy: Enter fires from keydown, so a second
   * submit can land before React re-renders the box as disabled.
   */
  const busyRef = useRef(false);
  const streamBufferRef = useRef<{ text: string; flushTimer: number | null; lastFlushAt: number }>({
    text: '',
    flushTimer: null,
    lastFlushAt: 0,
  });
  // Set while a turn streams so the visibility handler can force a catch-up flush.
  const flushRef = useRef<(() => void) | null>(null);
  const [files, setFiles]: any[] = useState([]);
  const [filePreviews, setFilePreviews]: any[] = useState([]);
  const textareaRef: any = useRef(null);
  const autosaveTimeoutRef = useRef<number | null>(null);
  const firestoreSavedChatIdRef = useRef<string | null>(null);
  const mainTitleForFirestoreRef = useRef<string>('');
  const prevHadConversationRouteRef = useRef(false);
  const location = useLocation();
  const history = useHistory();
  const [savedDeepChats, setSavedDeepChats] = useState<ISavedDeepChatListItem[]>([]);
  const [activeSavedChatId, setActiveSavedChatId] = useState<string | null>(null);

  // The composer stays locked for the whole turn: the saveChats create first,
  // then the stream, so a question can't be submitted twice.
  const isBusy = isSavingChat || isLoading;

  // Disabling the textarea blurs it, so hand focus back when the turn ends.
  const wasBusyRef = useRef(false);
  useEffect(() => {
    if (wasBusyRef.current && !isBusy && textareaRef.current) {
      textareaRef.current.focus();
    }
    wasBusyRef.current = isBusy;
  }, [isBusy]);

  const getDraftStorageKey = () => {
    const uid = localStorage.getItem('uid') || 'anonymous';
    return `${DEEP_RESEARCH_DRAFT_KEY_PREFIX}:${uid}`;
  };

  const buildFirestoreRecord = (mainTitle: string, messages: any[], apiChatId: string | number | null) => ({
    mainTitle,
    chatType: DEEP_RESEARCH_CHAT_TYPE,
    chatData: [
      {
        q: { title: mainTitle },
        attachFiles: [],
        timestamp: Math.floor(Date.now() / 1000),
      },
    ],
    deepResearchMessages: serializeDeepResearchMessages(messages),
    chatIds: apiChatId != null ? { deepResearchApiChatId: apiChatId } : {},
  });

  const refreshSavedDeepChatList = async () => {
    try {
      const response: any = await saveChats({ type: 'getAll', pageSize: 50 });
      const rows = response?.data?.data;
      if (!Array.isArray(rows)) {
        return;
      }
      // Accept rows whose chatType is 'deep-research' OR missing (older saves /
      // pre-deploy cloud function responses that don't project chatType).
      const list: ISavedDeepChatListItem[] = rows
        .filter((row: any) => {
          if (!row?.payload?.mainTitle) {
            return false;
          }
          const ct = row.payload.chatType;
          return ct == null || ct === DEEP_RESEARCH_CHAT_TYPE;
        })
        .map((row: any) => ({
          id: row.id,
          mainTitle: String(row.payload.mainTitle),
        }));
      // Merge with any optimistic local items so a freshly-created entry
      // stays visible even if the server response doesn't include it yet.
      setSavedDeepChats((prev) => {
        const ids = new Set(list.map((item) => item.id));
        const optimistic = prev.filter((item) => !ids.has(item.id));
        return [...optimistic, ...list];
      });
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error loading saved deep research chats:', error);
    }
  };

  const persistDeepResearchToFirestore = async (mainTitle: string, messages: any[], apiChatId: string | number | null) => {
    const record = buildFirestoreRecord(mainTitle, messages, apiChatId);
    try {
      if (firestoreSavedChatIdRef.current) {
        await saveChats({
          type: 'update',
          id: firestoreSavedChatIdRef.current,
          record,
        });
        return;
      }
      const response: any = await saveChats({
        type: 'create',
        record,
      });
      if (response?.data?.id) {
        const newId: string = response.data.id;
        firestoreSavedChatIdRef.current = newId;
        setActiveSavedChatId(newId);
        // Optimistically prepend so the sidebar shows the new chat immediately,
        // regardless of whether the listing endpoint returns chatType.
        setSavedDeepChats((prev) => {
          if (prev.some((item) => item.id === newId)) {
            return prev;
          }
          return [{ id: newId, mainTitle }, ...prev];
        });
        void refreshSavedDeepChatList();
        const nextPath = `/enviro-deep-research/${newId}`;
        if (history.location.pathname !== nextPath) {
          history.replace(nextPath);
        }
      }
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error saving deep research via savedChats:', error);
    }
  };

  /** Create Firestore row on first message of a new session (not when continuing an opened saved chat). */
  const ensureFirestoreDocForNewSession = async (question: string) => {
    if (firestoreSavedChatIdRef.current) {
      return;
    }
    mainTitleForFirestoreRef.current = question.trim();
    await persistDeepResearchToFirestore(mainTitleForFirestoreRef.current, [], null);
  };

  /** Ends the open stream client-side. The backend run continues and persists. */
  const abortActiveStream = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (streamBufferRef.current.flushTimer) {
      window.clearTimeout(streamBufferRef.current.flushTimer);
    }
    streamBufferRef.current = { text: '', flushTimer: null, lastFlushAt: 0 };
    flushRef.current = null;
  };

  const resetDeepResearchSession = () => {
    abortActiveStream();
    busyRef.current = false;
    setChatData([]);
    setChatId(null);
    setIsLoading(false);
    setIsSavingChat(false);
    setProgressLine(null);
    setSearchText('');
    setFilePreviews([]);
    setFiles([]);
    firestoreSavedChatIdRef.current = null;
    mainTitleForFirestoreRef.current = '';
    setActiveSavedChatId(null);
  };

  const startNewDeepChat = () => {
    resetDeepResearchSession();
    history.push('/enviro-deep-research');
  };

  const persistDraft = () => {
    try {
      const key = getDraftStorageKey();
      if (!searchText.trim()) {
        localStorage.removeItem(key);
        return;
      }
      const payload: IDeepResearchDraft = {
        searchText,
        updatedAt: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error autosaving deep research draft:', error);
    }
  };

  // Helper function to convert Blob to base64
  const blobToBase64 = (blob: any) => {
    return new Promise((resolve, reject) => {
      const reader: any = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Add attachment files to the search input, from either the picker or a drop.
   * @param selectedFiles
   */
  const appendAttachmentFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) { return; }

    const updatedFiles = [...files, ...selectedFiles];
    setFiles(updatedFiles);
    setFilePreviews([]); // Reset file previews

    // Process each file for previews
    const previews = await Promise.all(
      updatedFiles.map(async (file: any) => {
        if (IMAGE_MIME_TYPES.includes(file.type)) {
          const base64 = await blobToBase64(file);
          return { preview: base64, name: file.name, type: file.type };
        }
        return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
      })
    );

    setFilePreviews(previews);
  };

  /**
   * Picker adapter. The input's `accept` filter is advisory — the file dialog
   * can be switched to "All files" — so validate here as well as on drop.
   * @param event
   * @returns
   */
  const uploadAttachment = async (event: any) => {
    if (!event || !event.target) { return; }

    const selectedFiles = Array.from(event.target.files) as File[];
    event.target.value = ''; // Reset the file input value to allow selecting the same file again

    const { accepted, rejected } = partitionFiles(selectedFiles);
    notifyRejectedFiles(rejected);
    await appendAttachmentFiles(accepted);
  };

  // Drop anywhere on the page to attach, matching the paperclip's own lockout.
  const { isDragging } = useFileDrop({
    onFiles: appendAttachmentFiles,
    onReject: notifyRejectedFiles,
    disabled: isBusy || isExxonMobileEmail(context),
  });

  /**
   * Remove attachment files in search input
   * @param index
   */
  const removeAttachment = (index: number) => {
    setFiles((prevFiles: any) => prevFiles.filter((_: any, i: number) => i !== index));
    setFilePreviews((prevPreviews: any) => prevPreviews.filter((_: any, i: number) => i !== index));
  };

  useEffect(() => {
    const stateFiles = (location.state as any)?.files as File[] | undefined;
    if (!Array.isArray(stateFiles) || !stateFiles.length) return;
    const processFiles = async () => {
      const previews = await Promise.all(
        stateFiles.map(async (file: File) => {
          if (IMAGE_MIME_TYPES.includes(file.type)) {
            const base64 = await blobToBase64(file);
            return { preview: base64, name: file.name, type: file.type };
          }
          return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
        })
      );
      setFiles(stateFiles);
      setFilePreviews(previews);
    };
    void processFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistSnapshot = (snapshot: any[] | null, apiChatId: string | number | null) => {
    if (!snapshot) {
      return;
    }
    const titleRef = mainTitleForFirestoreRef.current?.trim();
    const firstUser = snapshot.find((m: any) => m.role === 'user');
    const rawFirst = typeof firstUser?.content === 'string' ? firstUser.content : '';
    const derivedTitle =
      titleRef ||
      rawFirst.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').trim() ||
      'Deep research';
    void persistDeepResearchToFirestore(derivedTitle, snapshot, apiChatId);
  };

  /**
   * Fallback path from the migration guide §6: the research run survives a lost
   * stream, so poll `/research/get_pred` for the same chat until it completes.
   * That endpoint answers with HTML, hence `format: 'html'` on the patch.
   */
  const recoverViaPolling = async (
    apiChatId: string | number,
    localUserId: string | null,
    patchMessage: (patch: any) => any[] | null,
    signal: AbortSignal,
  ): Promise<any[] | null> => {
    setProgressLine('Reconnecting — your research is still running…');
    try {
      const data = await pollResearchResult(apiChatId, localUserId, signal);
      if (signal.aborted) {
        return null;
      }
      if (!data) {
        return patchMessage({
          content:
            'Your research is taking longer than expected. It is still running — reopen this chat in a few minutes to see the report.',
          format: 'markdown',
          status: 'executing',
        });
      }
      return patchMessage({
        content: (data.content || 'No response received.').replace(/\n/g, '<br/>'),
        format: 'html',
        status: data.status,
        pdf: data.pdf ?? null,
        route: data.route,
        file_urls: data.file_urls,
      });
    } catch (error: any) {
      if (signal.aborted || error?.name === 'AbortError' || error?.message === 'aborted') {
        return null;
      }
      // tslint:disable-next-line:no-console
      console.log('Error polling deep research result:', error);
      return patchMessage({
        content: 'The connection dropped and the result could not be retrieved. Please reopen this chat shortly.',
        format: 'markdown',
      });
    }
  };

  const fetchResponse = async (manualSearchText?: string, forceFreshSearch = false) => {
    const typedQuery = forceFreshSearch ? '' : searchText.trim();
    const localSearchText = typedQuery || manualSearchText?.trim() || '';
    if (localSearchText === '') return;
    if (busyRef.current) return;
    busyRef.current = true;

    // Creating the Firestore row is awaited before the turn starts, so the
    // composer has to be locked here rather than at `setIsLoading` below.
    setIsSavingChat(true);
    try {
      await ensureFirestoreDocForNewSession(localSearchText);
    } catch (error) {
      // The save layer swallows its own failures; if it still throws, run the
      // research anyway rather than dropping the question.
      // tslint:disable-next-line:no-console
      console.log('Error preparing saved deep research chat:', error);
    }

    const userMessage = {
      content: localSearchText,
      role: 'user',
      attachFiles: filePreviews || [],
    };
    setChatData((prev) => [...prev, userMessage]);
    setSearchText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '34px';
    }
    setFilePreviews([]);
    setIsLoading(true);
    // Handed off to `isLoading` only here: React 16 doesn't batch across the
    // await above, so clearing it earlier would unlock the box for a frame.
    setIsSavingChat(false);
    setProgressLine(null);

    const effectiveChatId = forceFreshSearch ? null : chatId;
    const localUserId = localStorage.getItem('uid');

    // `/research/stream` is POST-first: EventSource can neither send the Bearer
    // token nor upload files, so every turn goes out as multipart.
    const formData = new FormData();
    formData.append('q', localSearchText);
    formData.append('chatStatus', effectiveChatId != null ? 'ongoing' : 'new');
    if (localUserId) {
      formData.append('userId', encodeURIComponent(localUserId));
    }
    if (effectiveChatId != null) {
      formData.append('chatId', String(effectiveChatId));
    }
    if (Array.isArray(files) && files.length) {
      files.forEach((file: any) => formData.append('files', file));
      setFiles([]);
    }

    abortActiveStream();
    const controller = new AbortController();
    abortRef.current = controller;

    // The assistant bubble is created on the first token / result so the
    // spinner isn't shadowed by an empty message while planning runs.
    const streamMsgId = `assistant-${Date.now()}`;
    let messageAppended = false;
    const ensureMessage = () => {
      if (messageAppended) return;
      messageAppended = true;
      setChatData((prev) => [
        ...prev,
        { id: streamMsgId, role: 'assistant', content: '', format: 'markdown' },
      ]);
    };

    const patchMessage = (patch: any): any[] | null => {
      ensureMessage();
      let snapshot: any[] | null = null;
      setChatData((prev) => {
        const next = prev.map((m) => (m.id === streamMsgId ? { ...m, ...patch } : m));
        snapshot = next;
        return next;
      });
      return snapshot;
    };

    streamBufferRef.current = { text: '', flushTimer: null, lastFlushAt: 0 };
    const flushTokens = () => {
      if (streamBufferRef.current.flushTimer) {
        window.clearTimeout(streamBufferRef.current.flushTimer);
        streamBufferRef.current.flushTimer = null;
      }
      streamBufferRef.current.lastFlushAt = Date.now();
      return patchMessage({ content: streamBufferRef.current.text });
    };
    flushRef.current = flushTokens;
    /**
     * Flush on token arrival once the cadence has elapsed, rather than relying
     * on a timer: background tabs clamp timers to >= 1s (and to ~1/min after
     * five minutes hidden), which froze the report until the tab was refocused.
     * Token arrival is driven by the network, so it is never throttled. The
     * timer only covers the trailing chunk after the last token.
     */
    const scheduleFlush = () => {
      if (Date.now() - streamBufferRef.current.lastFlushAt >= TOKEN_FLUSH_MS) {
        flushTokens();
        return;
      }
      if (streamBufferRef.current.flushTimer) return;
      streamBufferRef.current.flushTimer = window.setTimeout(() => {
        streamBufferRef.current.flushTimer = null;
        streamBufferRef.current.lastFlushAt = Date.now();
        patchMessage({ content: streamBufferRef.current.text });
      }, TOKEN_FLUSH_MS);
    };

    let liveChatId: string | number | null = effectiveChatId;
    let resultStatus: string | null = null;
    let sawError = false;
    let snapshotForPersist: any[] | null = null;

    try {
      const sawResult = await streamResearch(
        formData,
        {
          onMeta: (id) => {
            if (id != null) {
              liveChatId = id;
              setChatId(id);
            }
          },
          onStatus: (message) => setProgressLine(message),
          onToken: (content) => {
            ensureMessage();
            streamBufferRef.current.text += content;
            scheduleFlush();
          },
          onResult: ({ chatId: resultChatId, status, pdf }) => {
            if (resultChatId != null) {
              liveChatId = resultChatId;
              setChatId(resultChatId);
            }
            resultStatus = status;
            // An empty buffer on a terminal status means the turn produced no
            // prose (`executing` hand-off, or a genuinely empty answer).
            const streamed = streamBufferRef.current.text;
            snapshotForPersist = patchMessage({
              content: streamed || (status === 'executing' ? '' : 'No response received.'),
              status,
              pdf: pdf ?? null,
              format: 'markdown',
            });
          },
          onError: (message) => {
            sawError = true;
            snapshotForPersist = patchMessage({
              content: streamBufferRef.current.text || message,
              format: 'markdown',
            });
          },
          onDone: () => {
            snapshotForPersist = flushTokens() ?? snapshotForPersist;
            setProgressLine(null);
          },
        },
        controller.signal,
      );

      // A dropped connection or an already-running job leaves the turn without
      // a terminal payload — collect it from the polling endpoint instead. A
      // reported `error` is terminal, so it is not worth polling behind.
      const needsRecovery = !sawError && (!sawResult || resultStatus === 'executing');
      if (!controller.signal.aborted && liveChatId != null && needsRecovery) {
        snapshotForPersist =
          (await recoverViaPolling(liveChatId, localUserId, patchMessage, controller.signal)) ?? snapshotForPersist;
      }
    } catch (error: any) {
      if (controller.signal.aborted || error?.name === 'AbortError') {
        return;
      }
      // tslint:disable-next-line:no-console
      console.log('Error streaming research response:', error);
      if (liveChatId != null) {
        snapshotForPersist =
          (await recoverViaPolling(liveChatId, localUserId, patchMessage, controller.signal)) ?? snapshotForPersist;
      } else {
        snapshotForPersist = patchMessage({
          content: 'Something went wrong while starting your research. Please try again.',
          format: 'markdown',
        });
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
        setProgressLine(null);
        persistSnapshot(snapshotForPersist, liveChatId);
      }
      // Only the turn that still owns the stream unlocks the composer: an
      // aborted turn's `finally` can land after its replacement has locked it,
      // and whoever aborted it already released the lock itself.
      if (abortRef.current === controller) {
        busyRef.current = false;
        abortRef.current = null;
      }
      if (flushRef.current === flushTokens) {
        flushRef.current = null;
      }
    }
  };

  const openSavedDeepChat = async (id: string) => {
    try {
      const response: any = await saveChats({ type: 'get', id });
      const root = response?.data;
      if (!root || typeof root !== 'object' || !root.payload) {
        return;
      }
      const payload = root.payload;
      // Allow legacy saves (no chatType) so the URL-driven open still works
      // before/after the Cloud Function chatType projection is deployed.
      if (payload.chatType != null && payload.chatType !== DEEP_RESEARCH_CHAT_TYPE) {
        return;
      }
      const restored = payloadToChatData(payload);
      abortActiveStream();
      busyRef.current = false;
      setChatData(restored);
      setSearchText('');
      setIsLoading(false);
      setIsSavingChat(false);
      setProgressLine(null);
      // Ids are opaque: numeric on legacy saves, minted server-side on the
      // stream, so keep whatever shape was stored.
      const savedApiId = payload.chatIds?.deepResearchApiChatId;
      const restoredChatId =
        typeof savedApiId === 'number' || (typeof savedApiId === 'string' && savedApiId.trim())
          ? savedApiId
          : null;
      setChatId(restoredChatId);
      firestoreSavedChatIdRef.current = id;
      const titleFromPayload = typeof payload.mainTitle === 'string' ? payload.mainTitle : '';
      mainTitleForFirestoreRef.current = titleFromPayload;
      setActiveSavedChatId(id);

      if (!hasAssistantResults(restored)) {
        const question = extractFirstUserQuestion(restored) || titleFromPayload.trim();
        if (question) {
          setChatData([]);
          await fetchResponse(question, true);
        }
      }
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error opening saved deep research chat:', error);
    }
  };

  useEffect(() => {
    void refreshSavedDeepChatList();
  }, []);

  useEffect(() => {
    const m = matchPath<{ chatId: string }>(location.pathname, {
      path: '/enviro-deep-research/:chatId',
      exact: true,
    });
    const id = m?.params?.chatId;
    const hub = location.pathname === '/enviro-deep-research';

    if (id) {
      prevHadConversationRouteRef.current = true;
      if (id === firestoreSavedChatIdRef.current) {
        setActiveSavedChatId(id);
        return;
      }
      void openSavedDeepChat(id);
      return;
    }

    if (hub && prevHadConversationRouteRef.current) {
      resetDeepResearchSession();
    }
    prevHadConversationRouteRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    try {
      const key = getDraftStorageKey();
      const storedDraft = localStorage.getItem(key);
      if (!storedDraft) {
        return;
      }
      const parsedDraft = JSON.parse(storedDraft) as IDeepResearchDraft & { chatData?: unknown };
      const restoredText =
        parsedDraft && typeof parsedDraft.searchText === 'string' ? parsedDraft.searchText.trim() : '';
      if (parsedDraft && 'chatData' in parsedDraft) {
        localStorage.removeItem(key);
      }
      if (restoredText) {
        setSearchText(restoredText);
      }
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error restoring deep research draft:', error);
    }
  }, []);

  useEffect(() => {
    if (autosaveTimeoutRef.current) {
      window.clearTimeout(autosaveTimeoutRef.current);
    }
    autosaveTimeoutRef.current = window.setTimeout(() => {
      persistDraft();
    }, 1000);

    return () => {
      if (autosaveTimeoutRef.current) {
        window.clearTimeout(autosaveTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    const flushDraftOnUnload = () => {
      persistDraft();
    };
    window.addEventListener('beforeunload', flushDraftOnUnload);
    return () => {
      window.removeEventListener('beforeunload', flushDraftOnUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Leaving the page ends the stream client-side; the backend run continues and
  // its result is collected on the next open (see recoverViaPolling / §6).
  useEffect(() => {
    return () => abortActiveStream();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tokens keep arriving while the tab is hidden, but the trailing flush timer
  // is throttled — repaint everything buffered as soon as the tab is shown.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) {
        flushRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (!q) { return; }
    const decoded = decodeURIComponent(q);
    setSearchText(decoded);
    history.replace('/enviro-deep-research');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="enviro-deep-research">
      <FileDropOverlay visible={isDragging} />
      <Sidebar
        isHovered={isHovered}
        setIsHovered={setIsHovered}
        setSearchText={setSearchText}
        savedDeepChats={savedDeepChats}
        activeSavedChatId={activeSavedChatId}
        onSelectSavedChat={(selectedChatId) => history.push(`/enviro-deep-research/${selectedChatId}`)}
        onNewChat={startNewDeepChat}
      />
      <div className={`search-area-container cmn-deep-main-bg${!chatData.length ? ' search-area-container--landing' : ''}`}>
        <TopRightNav />
        {chatData.length ? (
          <ChatWindow
            chatData={chatData}
            isLoading={isBusy}
            progressLine={progressLine}
            onStartResearch={() => fetchResponse('Please go ahead')}
          />
        ) : (
          <>
            <p className="environmental-label">Deep Environmental Research & Environmental Report Generator</p>
            {/* <img src={Logo} alt="EnviroAI" className="enviro-ai-logo" /> */}
            <p className="enviro-subtitle">
              <span className="blue-text">Enviro</span>Deep<span className="blue-text">Research</span> <span className="gray-text">Pro</span>
            </p>
          </>
        )}
        <DeepSearchInput
          value={searchText}
          onChange={(e: any) => setSearchText(e.target.value)}
          onEnter={() => fetchResponse()}
          uploadAttachment={uploadAttachment}
          filePreviews={filePreviews}
          removeAttachment={removeAttachment}
          context={context}
          disabled={isBusy}
          textareaRef={textareaRef} />
      </div>
    </div>
  );
};

export default withAuthContext(EnviroDeepResearch);
